import { Command, Option } from "clipanion";
import { M8tCommand } from "../../lib/m8t-command.js";
import type { ListTeamResponse } from "@m8t-stack/api-contract";
import { apiCall } from "../../lib/api-client.js";
import { resolveGatewayContext } from "../../lib/gateway-context.js";
import {
  renderJson,
  renderTable,
  resolveOutputMode,
} from "../../lib/output.js";

export class TeamListCommand extends M8tCommand {
  static paths = [["team", "list"]];
  static usage = Command.Usage({
    description: "List team members.",
    details: "Lists every team member, grouped by handle, sorted alphabetically.",
    examples: [
      ["Pretty table", "$0 team list"],
      ["Machine-readable JSON", "$0 team list --output json"],
    ],
  });

  output = Option.String("--output");
  subscription = Option.String("--subscription", {
    description: "Azure subscription ID to discover gateway in (defaults to active az subscription).",
  });
  resourceGroup = Option.String("--resource-group", {
    description: "m8t resource group to disambiguate the gateway (multi-deployment subscriptions).",
  });

  async executeCommand(): Promise<number> {
    const mode = resolveOutputMode(
      this.output as "pretty" | "json" | "auto" | undefined,
      this.context.stdout as NodeJS.WriteStream,
    );
    const interactive = (this.context.stdout as NodeJS.WriteStream).isTTY === true;

    const ctx = await resolveGatewayContext({ interactive, subscriptionId: this.subscription, resourceGroup: this.resourceGroup });
    const data = await apiCall<ListTeamResponse>(
      { gatewayUrl: ctx.gatewayUrl, gatewayClientId: ctx.gatewayClientId },
      { method: "GET", path: "/api/team" },
      (msg) => this.context.stderr.write(msg + "\n"),
    );

    if (mode === "json") {
      this.context.stdout.write(renderJson(data) + "\n");
      return 0;
    }

    if (data.members.length === 0) {
      this.context.stdout.write(
        "no team members yet — try 'm8t team add <handle> --display \"<name>\" --<channel> <id>'.\n",
      );
      return 0;
    }

    interface Row extends Record<string, unknown> {
      handle: string;
      display: string;
      telegram: string;
      slack: string;
      teams: string;
      added: string;
      addedBy: string;
    }

    const rows: Row[] = data.members.map((m) => ({
      handle: m.handle,
      display: m.displayName,
      telegram: m.identities.telegram ?? "-",
      slack: m.identities.slack ?? "-",
      teams: m.identities.teams ?? "-",
      added: m.addedAt.slice(0, 10),
      addedBy: m.addedBy,
    }));

    const table = renderTable<Row>(rows, [
      { key: "handle", label: "HANDLE" },
      { key: "display", label: "DISPLAY" },
      { key: "telegram", label: "TELEGRAM" },
      { key: "slack", label: "SLACK" },
      { key: "teams", label: "TEAMS" },
      { key: "added", label: "ADDED" },
      { key: "addedBy", label: "ADDED BY" },
    ]);
    this.context.stdout.write(table + "\n");
    return 0;
  }
}
